<template>
    <section class="tk-engagements-panel">
        <div class="tk-engagements-panel__header">
            <h2>Groups</h2>
            <q-btn
                color="primary"
                no-caps
                unelevated
                icon="add"
                label="Create Group"
                :disable="!availableServiceOptions.length"
                @click="openCreate"
            />
        </div>

        <div class="tk-engagements-filters row q-col-gutter-md">
                    <div class="col-12 col-md-5">
                        <q-input
                            v-model="filters.q"
                            outlined
                            dense
                            clearable
                            debounce="300"
                            label="Search Groups"
                            @update:model-value="refresh"
                        >
                            <template v-slot:prepend><q-icon name="search" /></template>
                        </q-input>
                    </div>
                    <div class="col-12 col-md-4">
                        <q-select
                            v-model="filters.service"
                            :options="serviceOptions"
                            outlined
                            dense
                            clearable
                            emit-value
                            map-options
                            label="Group Type"
                            @update:model-value="refresh"
                        />
                    </div>
                    <div class="col-12 col-md-3">
                        <q-select
                            v-model="filters.status"
                            :options="statusOptions"
                            outlined
                            dense
                            clearable
                            emit-value
                            map-options
                            label="Status"
                            @update:model-value="refresh"
                        />
                    </div>
        </div>

        <q-table
            :rows="rows"
            :columns="columns"
            row-key="id"
            flat
            dense
            :loading="loading"
            :pagination="{ rowsPerPage: 10 }"
            :rows-per-page-options="[10, 20, 50]"
            class="app-data-table"
            @row-click="openGroup"
        >
            <template v-slot:body-cell-notes="props">
                <q-td :props="props" class="tk-groups-table__clamped-cell">
                    {{ notesSummary(props.row.notes) }}
                </q-td>
            </template>
            <template v-slot:body-cell-status="props">
                <q-td :props="props">
                    <q-badge
                        rounded
                        outline
                        :color="props.row.status_code === 'active' ? 'positive' : 'grey-7'"
                        :label="props.row.status_code === 'active' ? 'Active' : 'Concluded'"
                    />
                </q-td>
            </template>
            <template v-slot:no-data>
                <div class="full-width row flex-center text-grey-7 q-pa-md">
                    No groups found.
                </div>
            </template>
        </q-table>

        <TkRightDrawer
            v-model="showCreate"
            title="Create Group"
            subtitle="Create a group engagement and assign its coordinator."
            width="760px"
        >
            <q-form ref="createForm" @submit.prevent="createGroup">
                    <q-card-section>
                        <div class="row q-col-gutter-md">
                            <div class="col-12 col-md-6 tk-field">
                                <p class="q-mb-xs"><strong>Group Type</strong></p>
                                <q-select
                                    v-model="create.service_code"
                                    :options="availableServiceOptions"
                                    outlined
                                    dense
                                    emit-value
                                    map-options
                                    lazy-rules="ondemand"
                                    hide-bottom-space
                                    :rules="[value => !!value || 'Group Type is required.']"
                                    @update:model-value="onCreateServiceChange"
                                />
                            </div>
                            <div class="col-12 col-md-6 tk-field">
                                <p class="q-mb-xs"><strong>Group Name</strong></p>
                                <q-select
                                    v-model="create.group_type_code"
                                    :options="createGroupTypeOptions"
                                    outlined
                                    dense
                                    emit-value
                                    map-options
                                    lazy-rules="ondemand"
                                    hide-bottom-space
                                    :disable="!create.service_code"
                                    :rules="[value => !!value || 'Group Name is required.']"
                                    @update:model-value="onCreateTypeChange"
                                />
                            </div>
                            <div v-if="selectedCreateGroupType?.allows_custom_name" class="col-12 col-md-6 tk-field">
                                <p class="q-mb-xs"><strong>Custom Group Name</strong></p>
                                <q-input
                                    v-model="create.custom_name"
                                    outlined
                                    dense
                                    maxlength="255"
                                    lazy-rules="ondemand"
                                    hide-bottom-space
                                    :rules="[value => !!String(value || '').trim() || 'Group Name is required.']"
                                />
                            </div>
                            <div class="col-12 col-md-6 tk-field">
                                <p class="q-mb-xs"><strong>Coordinator</strong></p>
                                <q-select
                                    v-model="create.coordinator_id"
                                    :options="staffOptions"
                                    outlined
                                    dense
                                    emit-value
                                    map-options
                                    lazy-rules="ondemand"
                                    hide-bottom-space
                                    :rules="[value => !!value || 'Coordinator is required.']"
                                />
                            </div>
                            <div class="col-12 tk-field">
                                <p class="q-mb-xs"><strong>Notes</strong></p>
                                <q-input
                                    v-model="create.notes"
                                    outlined
                                    dense
                                    type="textarea"
                                    rows="8"
                                />
                            </div>
                        </div>
                    </q-card-section>
                    <q-card-actions align="right">
                        <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                        <q-btn unelevated no-caps color="primary" icon="save" label="Create Group" type="submit" :loading="saving" />
                    </q-card-actions>
            </q-form>
        </TkRightDrawer>
    </section>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import { formatDate, staffOption } from "@/services/util/tkClients";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";

const buildCreateForm = () => ({
    service_code: null,
    group_type_code: null,
    custom_name: "",
    coordinator_id: null,
    notes: "",
    status_code: "active",
});

const errorMessage = (error, fallbackMessage) =>
    error.response?.data?.error || error.response?.statusText || fallbackMessage;

export default {
    name: "TkGroups",
    components: { TkRightDrawer },
    data() {
        return {
            loading: false,
            saving: false,
            showCreate: false,
            rows: [],
            services: [],
            groupTypes: [],
            staff: [],
            filters: { q: "", service: null, status: "active" },
            create: buildCreateForm(),
            statusOptions: [
                { label: "Active", value: "active" },
                { label: "Concluded", value: "concluded" },
            ],
            columns: [
                { name: "group_name", label: "Group Name", field: "group_name", align: "left", sortable: true },
                { name: "service", label: "Group Type", field: "service_name", align: "left", sortable: true },
                { name: "coordinator", label: "Coordinator", field: row => row.coordinator_name || row.coordinator_email || "-", align: "left", sortable: true },
                { name: "notes", label: "Notes", field: "notes", align: "left" },
                { name: "most_recent", label: "Most recent session", field: row => formatDate(row.most_recent_session_date), align: "left", sortable: true },
                { name: "sessions", label: "Sessions", field: "session_count", align: "right", sortable: true },
                { name: "status", label: "Status", field: "status_code", align: "left", sortable: true },
            ],
        };
    },
    computed: {
        serviceOptions() {
            return this.services.map(service => ({ label: service.name, value: service.code }));
        },
        availableServiceOptions() {
            const availableCodes = new Set(this.groupTypes.map(type => type.service_code));
            return this.serviceOptions.filter(option => availableCodes.has(option.value));
        },
        createGroupTypeOptions() {
            return this.groupTypes
                .filter(type => type.service_code === this.create.service_code)
                .map(type => ({ label: type.name, value: type.code }));
        },
        selectedCreateGroupType() {
            return this.groupTypes.find(type => type.code === this.create.group_type_code) || null;
        },
        staffOptions() {
            return this.staff.map(staffOption);
        },
        currentStaffId() {
            const email = String(token.getEmail() || "").toLowerCase();
            return this.staff.find(member => String(member.email || "").toLowerCase() === email)?.id || null;
        },
    },
    async mounted() {
        await this.loadOptions();
        await this.refresh();
    },
    methods: {
        notesSummary(value) {
            const text = String(value || "").trim();
            return text.length > 100 ? `${text.slice(0, 97)}...` : text || "-";
        },
        async loadOptions() {
            try {
                const [lookupResponse, typeResponse, staffResponse] = await Promise.all([
                    api.tkPeople.getEngagementLookups(),
                    api.groupEngagements.listGroupTypes({}),
                    api.users.getAppStaffList(),
                ]);
                this.services = lookupResponse.data.services || [];
                this.groupTypes = typeResponse.data || [];
                this.staff = staffResponse.data || [];
            } catch (error) {
                notify.error(errorMessage(error, "Failed to load group options"), "top");
            }
        },
        async refresh() {
            this.loading = true;
            try {
                const response = await api.groupEngagements.listGroups({
                    q: this.filters.q || undefined,
                    service: this.filters.service || undefined,
                    status: this.filters.status || undefined,
                    limit: 200,
                });
                this.rows = response.data || [];
            } catch (error) {
                notify.error(errorMessage(error, "Failed to load groups"), "top");
            } finally {
                this.loading = false;
            }
        },
        openCreate() {
            this.create = buildCreateForm();
            this.create.service_code = this.filters.service
                && this.availableServiceOptions.some(option => option.value === this.filters.service)
                ? this.filters.service
                : this.availableServiceOptions[0]?.value || null;
            this.onCreateServiceChange(this.create.service_code);
            this.create.coordinator_id = this.currentStaffId;
            this.showCreate = true;
        },
        onCreateServiceChange() {
            this.create.group_type_code = this.createGroupTypeOptions[0]?.value || null;
            this.onCreateTypeChange(this.create.group_type_code);
        },
        onCreateTypeChange() {
            this.create.custom_name = "";
        },
        async createGroup() {
            if (!await this.$refs.createForm?.validate()) return;
            this.saving = true;
            try {
                const response = await api.groupEngagements.createGroup({
                    ...this.create,
                    status_code: "active",
                    notes: this.create.notes.trim() || null,
                });
                this.showCreate = false;
                await this.$router.push({ name: "tkGroupDetail", params: { groupId: response.data.id } });
            } catch (error) {
                notify.error(errorMessage(error, "Failed to create group"), "top");
            } finally {
                this.saving = false;
            }
        },
        openGroup(event, row) {
            if (event?.defaultPrevented) return;
            this.$router.push({ name: "tkGroupDetail", params: { groupId: row.id } });
        },
    },
};
</script>

<style scoped>

</style>
